import { ArrayField } from 'react-hook-form';
import { IntlShape } from 'react-intl';
import { Grid } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import {
  Clear,
  Done,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@material-ui/icons';
import moment from 'moment';
import { ProjectCategories } from 'components/Dialog/types';
import { ProjectData } from 'modules/WorkTime/types';
import {
  mapStringToDuration,
  mapStringToISODuration,
} from 'utils/durationFunctions';
import { returnHourString } from 'utils/formatters/returnHourString';
import {
  getContractorAbsenceLabel,
  isRetiredContractorAbsence,
  isTimeOffProjectCategory,
} from 'utils/projectCategories';
import {
  getSubmittedHours,
  getWorkingHours,
  parseWorkingHours,
} from 'utils/reportingMode';
import { getFullReportDayId } from './fullReportDayId';
import {
  CustomFieldsRowProps,
  FullReportDay,
  InvalidWorktimeProps,
  ParsedDaysProps,
  RenderActionIconsProps,
  SummaryStatus,
  TableCellData,
} from './types';
import {
  ActionIconWrapper,
  ActivateIcon,
  AddIcon,
  BoldedText,
  BorderRow,
  DeactivateIcon,
  HeaderTooltipText,
  LeftCellWrapper,
  RightCellWrapper,
  TableIconButton,
  Tooltip,
  TrashIcon,
} from './Table.css';

export const renderTableCellData = (entry: TableCellData) => {
  switch (typeof entry) {
    case 'string':
      return entry;
    case 'number':
      return entry.toString();
    case 'boolean':
      return entry ? <Done /> : <Clear />;
  }
};

export const returnHideIcon = (intl: IntlShape) => (
  <>
    <KeyboardArrowUp />
    <Typography>
      {intl.formatMessage({
        id: 'TABLE.BODY.DETAILS_HIDE',
      })}
    </Typography>
  </>
);

export const returnShowIcon = (intl: IntlShape) => (
  <>
    <KeyboardArrowDown />
    <Typography>
      {intl.formatMessage({
        id: 'TABLE.BODY.DETAILS_SHOW',
      })}
    </Typography>
  </>
);

export const CustomFieldsRow = ({
  intl,
  onAddCustomFieldClicked,
  index,
  isAccountPrivileged,
}: CustomFieldsRowProps) => (
  <BorderRow>
    <LeftCellWrapper>
      {`${intl.formatMessage({
        id: 'CUSTOM_FIELDS.TITLE',
      })}`}
    </LeftCellWrapper>
    {isAccountPrivileged && (
      <RightCellWrapper>
        <TableIconButton
          id="addCustomFieldsButton"
          onClick={() => onAddCustomFieldClicked(index)}
        >
          <Tooltip
            title={
              <HeaderTooltipText>
                {intl.formatMessage({
                  id: 'CUSTOM_FIELDS.DIALOG_TITLE',
                })}
              </HeaderTooltipText>
            }
            placement="top"
          >
            <AddIcon />
          </Tooltip>
        </TableIconButton>
      </RightCellWrapper>
    )}
  </BorderRow>
);

export function renderActionIcons<T extends Record<string, any>>({
  renderEditIcon,
  setDialogData,
  index,
  data,
  intl,
  entry,
  handleShowConfimationDialog,
  isArchivable,
  isDeletable,
  elementToRemove,
  handleActivateClicked,
  isAllowedForActions,
  isJitOther,
}: RenderActionIconsProps<T>) {
  const onActivateClicked = () => {
    handleShowConfimationDialog();
    setDialogData(data[index]);
    handleActivateClicked && handleActivateClicked();
  };

  const onDeleteClicked = () => {
    setDialogData(data[index]);
    handleShowConfimationDialog();
  };

  const trashIconLabel = elementToRemove && elementToRemove.toUpperCase();

  const getInfo = () => {
    switch (true) {
      case Boolean(entry?.status !== 'ACTIVE' && entry.project):
        return 'TABLE_ACTIONS.ACTIVATE_INFO_PROJECT';
      case Boolean(entry?.status !== 'ACTIVE' && !entry.project):
        return 'TABLE_ACTIONS.ACTIVATE_INFO_CLIENT';
      case Boolean(entry.project):
        return 'TABLE_ACTIONS.DEACTIVATE_INFO_PROJECT';
      default:
        return 'TABLE_ACTIONS.DEACTIVATE_INFO_CLIENT';
    }
  };

  const renderIcons = () => {
    if (isArchivable) {
      return (
        <Tooltip
          title={
            <HeaderTooltipText>
              {intl.formatMessage({
                id: getInfo(),
              })}
            </HeaderTooltipText>
          }
          placement="top"
        >
          <div>
            <TableIconButton
              id="activationButton"
              onClick={onActivateClicked}
              disabled={!isAllowedForActions || isJitOther}
            >
              {entry?.status === 'ACTIVE' ? (
                <DeactivateIcon />
              ) : (
                <ActivateIcon />
              )}
            </TableIconButton>
          </div>
        </Tooltip>
      );
    } else if (trashIconLabel) {
      return (
        <Tooltip
          title={
            <HeaderTooltipText>
              {intl.formatMessage({
                id: isDeletable
                  ? `TABLE_ACTIONS.${trashIconLabel}_DELETABLE`
                  : `TABLE_ACTIONS.${trashIconLabel}_NOT_DELETABLE`,
              })}
            </HeaderTooltipText>
          }
          placement="top"
        >
          <span>
            <TableIconButton
              id="deleteButton"
              onClick={onDeleteClicked}
              disabled={!isDeletable}
            >
              <TrashIcon />
            </TableIconButton>
          </span>
        </Tooltip>
      );
    } else {
      return (
        <TableIconButton
          id="deleteButton"
          onClick={onDeleteClicked}
          disabled={!isDeletable}
        >
          <TrashIcon />
        </TableIconButton>
      );
    }
  };

  return (
    <ActionIconWrapper>
      {isAllowedForActions && renderEditIcon(data, index)}
      <>{renderIcons()}</>
    </ActionIconWrapper>
  );
}

export const getWorkTimeDuration = (
  startTime: string,
  endTime: string,
  breakTime: string,
) => {
  if (
    mapStringToDuration(breakTime) <
    mapStringToDuration(endTime).subtract(mapStringToDuration(startTime))
  ) {
    return mapStringToDuration(endTime)
      .subtract(mapStringToDuration(startTime))
      .subtract(mapStringToDuration(breakTime));
  }
  return '';
};

export const getWorkTimeMidnightDuration = (
  startTime: string,
  breakTime: string,
) => {
  const midnightDuration = moment
    .duration('23:59')
    .subtract(mapStringToDuration(startTime));
  if (mapStringToDuration(breakTime) < midnightDuration) {
    return midnightDuration
      .subtract(mapStringToDuration(breakTime))
      .add(1, 'minutes');
  }
  return '';
};

const checkIfMidnight = (hour: number) => {
  if (hour === 0) {
    return 24;
  } else {
    return hour;
  }
};

const areHoursOverlapping = (
  currentStartTime: number | null,
  currentEndTime: number | null,
  previousStartTime: number | null,
  previousEndTime: number | null,
) => {
  if (
    typeof currentStartTime !== 'number' ||
    typeof currentEndTime !== 'number' ||
    typeof previousStartTime !== 'number' ||
    typeof previousEndTime !== 'number'
  ) {
    return;
  }

  const validStartTime =
    (currentStartTime >= previousStartTime &&
      currentStartTime >= checkIfMidnight(previousEndTime)) ||
    (currentStartTime <= previousStartTime &&
      checkIfMidnight(currentEndTime) <= previousStartTime);

  const validEndTime =
    (checkIfMidnight(currentEndTime) > previousStartTime &&
      checkIfMidnight(currentEndTime) >= checkIfMidnight(previousEndTime)) ||
    checkIfMidnight(currentEndTime) <= previousStartTime;

  return !validStartTime || !validEndTime;
};

// A row as typed records a day without work: 0 hours in hours-only (B2B and
// civil-contract) reporting. An empty row does not, and time-range reporting
// has no day without work.
export const isDayWithoutWorkRow = (hoursOnly: boolean, hours: unknown) =>
  hoursOnly && getWorkingHours(hours) === 0;

type CategorizedProject = { projectCategory?: string | null };

export interface ReportedWorkRow {
  startTime?: unknown;
  endTime?: unknown;
  hours?: unknown;
  // A form row holds the selected option; a saved row also has
  // projectBasicInfo.
  project?: CategorizedProject | string | null;
  projectBasicInfo?: CategorizedProject | null;
}

// Whether saving keeps the client and project of a row. The backend keeps them
// on a row that reports something: both clock times, an hours amount in
// hours-only reporting (0 included, a day without work) or an absence
// project, which it saves as a standard day. On any other row it clears the
// client, project and times and still answers 200. An hours amount that does
// not parse counts as reported, so saving explains it.
export const keepsReportedWork = (row: ReportedWorkRow, hoursOnly: boolean) => {
  const project = typeof row.project === 'object' ? row.project : undefined;
  if (
    isTimeOffProjectCategory(
      project?.projectCategory ?? row.projectBasicInfo?.projectCategory,
    )
  ) {
    return true;
  }
  if (hoursOnly) {
    const parsed = parseWorkingHours(row.hours);
    return !parsed.valid || parsed.hours !== null;
  }
  return Boolean(row.startTime) && Boolean(row.endTime);
};

// A row that reports nothing as edited and reported nothing when loaded:
// saving keeps none of its client, project, times or hours, so edits of them
// leave it unchanged (owner, 12.10). Clearing a saved row is a deletion and
// stays a change.
export const discardsWorkEdits = (
  row: ReportedWorkRow,
  savedRow: ReportedWorkRow,
  hoursOnly: boolean,
) =>
  !keepsReportedWork(row, hoursOnly) && !keepsReportedWork(savedRow, hoursOnly);

const WORK_FIELDS = [
  'client',
  'project',
  'startTime',
  'endTime',
  'breakTime',
  'manHours',
  'hours',
  'savedMillis',
  'multiplicator',
];

// Whether the edited report holds a change that saving keeps. dirtyRows are
// the dirty fields of the days (react-hook-form), rows the days as edited and
// savedRows as loaded, by index. Work fields of a row that discards them do
// not count; anything else does, such as a comment (the backend keeps it) or
// an added or removed row.
export const hasKeptChanges = (
  dirtyRows: Array<Record<string, unknown> | undefined> | undefined,
  rows: Array<ReportedWorkRow | undefined>,
  savedRows: Array<ReportedWorkRow | undefined>,
  hoursOnly: boolean,
) =>
  (dirtyRows || []).some((dirty, index) => {
    const changed = Object.keys(dirty || {}).filter((key) => dirty?.[key]);
    if (!changed.length) {
      return false;
    }
    const row = rows[index];
    const savedRow = savedRows[index];
    if (!row || !savedRow || !discardsWorkEdits(row, savedRow, hoursOnly)) {
      return true;
    }
    return changed.some((key) => !WORK_FIELDS.includes(key));
  });

// A working day that is still empty or reports hours starts on the default
// client and project. A B2B or civil-contract day without work may have
// neither (owner decision 02.10), so one left or cleared without them keeps
// no client and project.
export const shouldUseDefaultClientAndProject = (
  entry: {
    hours?: unknown;
    startTime?: string | Date | null;
  },
  isFreeDay: boolean,
  hoursOnly: boolean,
) =>
  !isFreeDay &&
  !entry.startTime &&
  !isDayWithoutWorkRow(hoursOnly, entry.hours);

export const getInvalidWorktime = (days: ParsedDaysProps[]) => {
  const getWorktime = (time: string | null) => {
    if (time) {
      return moment.duration(time).asHours();
    }
    return null;
  };
  const invalidWorktime: InvalidWorktimeProps[] = [];

  const checkIfInvalidWorktime = () =>
    days?.map((entry, _, arrayOfDays) => {
      const entryStartTime = getWorktime(entry.startTime);
      const entryEndTime = getWorktime(entry.endTime);

      return arrayOfDays.map((nextEntry) => {
        if (entry.date === nextEntry.date && entry.id !== nextEntry.id) {
          const nextEntryStartTime = getWorktime(nextEntry.startTime);
          const nextEntryEndTime = getWorktime(nextEntry.endTime);

          if (
            areHoursOverlapping(
              nextEntryStartTime,
              nextEntryEndTime,
              entryStartTime,
              entryEndTime,
            )
          ) {
            invalidWorktime.push({ type: 'overlapped', id: Number(entry.id) });
          }

          // Hours-only entries carry no clock times.
          if (entryStartTime !== null && entryStartTime === entryEndTime) {
            invalidWorktime.push({ type: 'equal', id: Number(entry.id) });
          }
        }
        return null;
      });
    });
  checkIfInvalidWorktime();
  return invalidWorktime;
};

export const getInvalidWorktimeTitle = (
  intl: IntlShape,
  invalidWorktime: InvalidWorktimeProps[],
  id: number,
) => {
  const isEqual =
    invalidWorktime.find((day) => day.type === 'equal')?.id === id;
  let type = 'OVERLAPPING_HOURS';
  if (isEqual) {
    type = 'EQUAL_HOURS';
  }

  return intl.formatMessage({
    id: `WORK_TIME_TAB.${type}.TOOLTIP`,
  });
};

const getTimeOffTooltip = (category: ProjectCategories) => {
  switch (category) {
    // For employment contracts DAY_OFF is unpaid leave.
    case ProjectCategories.dayOff:
      return 'PROJECT_TOOLTIP.LABEL.DAY_OFF';
    case ProjectCategories.vacations:
      return 'PROJECT_TOOLTIP.LABEL.VACATIONS';
    case ProjectCategories.plannedServiceBreak:
      return 'PROJECT_TOOLTIP.LABEL.PLANNED_SERVICE_BREAK';
    default:
      return undefined;
  }
};

// B2B and civil-contract consultants are offered only "Service break" among
// absence projects. Their legacy DAY_OFF and UNPLANNED_SERVICE_BREAK projects
// stay out of pickers; entries already saved on them still display.
export const addLabelsForTimeOffProjects = (
  projectsData: ProjectData[],
  contractType?: string | null,
  intl?: IntlShape,
) =>
  projectsData.map((project) => {
    if (isRetiredContractorAbsence(project.projectCategory, contractType)) {
      return { ...project, hiddenInPicker: true };
    }
    const tooltipMessage = getTimeOffTooltip(project.projectCategory);
    if (!tooltipMessage) {
      return project;
    }
    const label = getContractorAbsenceLabel(
      project.projectCategory,
      contractType,
    );
    return {
      ...project,
      name: label && intl ? intl.formatMessage({ id: label }) : project.name,
      tooltipMessage,
    };
  });

// Shows a saved service-break entry of a B2B or civil contract as "Service
// break"; other entries keep their project name.
export const labelAbsenceDay = <
  T extends Partial<Pick<FullReportDay, 'project' | 'projectBasicInfo'>>,
>(
  day: T,
  contractType: string | null | undefined,
  intl: IntlShape,
): T => {
  const label = getContractorAbsenceLabel(
    day.projectBasicInfo?.projectCategory ?? day.project?.projectCategory,
    contractType,
  );
  if (!label) {
    return day;
  }
  const name = intl.formatMessage({ id: label });
  return {
    ...day,
    project: day.project && { ...day.project, name },
    projectBasicInfo: day.projectBasicInfo && {
      ...day.projectBasicInfo,
      name,
    },
  };
};

export const projectsFilter = (
  projectsData: ProjectData[],
  client?: string | null,
) => {
  return projectsData.filter(
    (data) => data.client.clientName === client && !data.hiddenInPicker,
  );
};

const getProjectsForClient = (
  data: FullReportDay[],
  projectsData: ProjectData[],
  defaultClientName: string,
) => {
  return data.map((day) => {
    const getClient = () => {
      return day?.client?.name ?? defaultClientName;
    };
    return {
      dayId: getFullReportDayId(day),
      projects: projectsFilter(projectsData, getClient()),
    };
  });
};

export const getInitialProjects = (
  data: FullReportDay[],
  projectsData: ProjectData[],
  defaultClientName: string,
) => {
  return getProjectsForClient(data, projectsData, defaultClientName);
};

export const getNewEntryProjects = (
  newEntry: FullReportDay,
  projectsData: ProjectData[],
  defaultClientName: string | null,
) => {
  return {
    dayId: newEntry.dayId,
    projects: projectsFilter(projectsData, defaultClientName),
  };
};

// Hours-only entries carry the amount and no clock times; other entries
// keep sending start, end and break as before.
export const getFullReportDayTimes = (
  entry: Pick<
    FullReportDay,
    'startTime' | 'endTime' | 'breakTime' | 'hours' | 'savedMillis'
  >,
  hoursOnly: boolean,
) =>
  hoursOnly
    ? {
        startTime: null,
        endTime: null,
        breakTime: null,
        hours: getSubmittedHours(entry.hours, entry.savedMillis),
      }
    : {
        startTime: returnHourString(entry.startTime),
        endTime: returnHourString(entry.endTime),
        breakTime: moment(entry.breakTime).isValid()
          ? mapStringToISODuration(entry.breakTime)
          : 'PT0S',
      };

// An extra (split) entry left without times or hours, comment and
// attachments is left out of the save; the backend then deletes it, as a
// single cleared entry is, so the date keeps one empty row (change request
// of 15.10, item 4). A day without work (0 hours) is not empty, and neither
// is an absence project, which the backend saves as a standard day.
export const isClearedExtraEntry = (
  entry: Pick<
    FullReportDay,
    | 'parentId'
    | 'startTime'
    | 'endTime'
    | 'breakTime'
    | 'hours'
    | 'savedMillis'
    | 'comment'
    | 'attachments'
  > &
    Pick<ReportedWorkRow, 'project' | 'projectBasicInfo'>,
  hoursOnly: boolean,
) => {
  if (!entry.parentId || keepsReportedWork(entry, hoursOnly)) {
    return false;
  }
  const times = getFullReportDayTimes(entry, hoursOnly);
  return (
    !times.startTime &&
    !times.endTime &&
    (times.hours === null || times.hours === undefined) &&
    !entry.comment?.trim() &&
    !entry.attachments?.length
  );
};

// A client or project without an id is sent as null: a day without work may
// be saved without them.
export const getFormattedFullReportData = (parsedDays: ParsedDaysProps[]) =>
  parsedDays.map((day) => {
    const clientId = day.client?.value ?? day.client?.id;
    const projectId = day.project?.value ?? day.project?.id;

    return {
      ...day,
      client: clientId == null ? null : { ...day.client, id: clientId },
      project: projectId == null ? null : { ...day.project, id: projectId },
    };
  });

export const getBankHolidayTooltip = (
  entry: Partial<ArrayField<Record<string, any>, 'id'>>,
) => {
  const title = entry.bankHolidayDTOS.map((el: { title: string }) => el.title);
  const description = entry.bankHolidayDTOS.map(
    (el: { description: string }) => el.description,
  );
  return (
    <Grid direction="row">
      <Grid>
        <BoldedText>{title}</BoldedText>
      </Grid>
      <Grid>
        <span>{description}</span>
      </Grid>
    </Grid>
  );
};

export const getSummaryButtonText = (
  approveAndSend: string,
  summaryStatus?: SummaryStatus,
) =>
  summaryStatus === SummaryStatus.NEEDS_LEADER_APPROVAL ||
  summaryStatus === SummaryStatus.WAITING_FOR_LEADER_APPROVAL
    ? 'WORK_TIME_TAB.REQUEST_FOR_APPROVAL'
    : approveAndSend;

// A 409 means entries still point at an order that ended or was cancelled
// before them; the backend lists the dates.
export const getApprovalErrorMessage = (
  intl: IntlShape,
  status: number,
  data: { errorMessage?: string; message?: string } | undefined,
  label: string,
) => {
  const detail = data?.errorMessage || data?.message || '';
  if (status === 409) {
    return [
      intl.formatMessage({ id: 'WORK_TIME.NEEDS_CORRECTION.SUBMIT_BLOCKED' }),
      detail,
    ]
      .filter(Boolean)
      .join(' ');
  }
  return detail || intl.formatMessage({ id: `${label}.TOAST_DENIED` });
};

export const getSendReportToastMessage = (summaryStatus?: SummaryStatus) =>
  summaryStatus === SummaryStatus.NEEDS_LEADER_APPROVAL ||
  summaryStatus === SummaryStatus.WAITING_FOR_LEADER_APPROVAL
    ? 'SUMMARY.REQUEST_APPROVAL'
    : 'SUMMARY.SENDING';
